// Prüft ein JPEG auf Metadaten (CLAUDE.md, Datenschutz; PLAN.md, Abschnitt
// 3, „Fotos und Dateiablage“): Standort, Kamera, Zeit und alles andere, was
// ein Handy in ein Foto schreibt. Als Original gilt das verkleinerte Foto
// OHNE Metadaten – der Server nimmt nur solche an, und ein automatischer Test
// prüft das.
//
// Ein JPEG besteht aus Abschnitten, jeder beginnt mit 0xFF und einer Kennung.
// Metadaten stecken in den „APP“-Abschnitten (0xE1 bis 0xEF) und in
// Kommentaren (0xFE). Erlaubt bleiben APP0 (JFIF, nur Bildformat) und APP2
// mit einem Farbprofil (ICC) – beide enthalten nichts über Ort, Zeit oder
// Gerät. Alles andere zählt als Metadaten.
//
// Seit 19.10.2026 (O39) entfernt `jpegMetadatenEntfernen` solche Abschnitte
// Byte für Byte – unabhängig davon, welcher Browser das Foto neu kodiert hat.
// Anlass: Am iPhone schreibt Safari beim Verkleinern eigene Exif- und
// IPTC-Blöcke hinein, am Laptop nicht (DECISIONS.md, 18.10., Punkt 7).
// Die Bilddaten selbst bleiben unverändert; dazu sagt die Funktion, was
// drinsteckte und ob ein Standort dabei war.
//
// Seit E2, Schritt 2 dazu zwei Lese-Hilfen: `jpegAufnahmezeit` holt die
// Aufnahmezeit aus dem Rohfoto, BEVOR es verkleinert wird (danach hat es
// keine Metadaten mehr), und `jpegMasse` liest Breite und Höhe aus dem
// Bildkopf – so braucht der Server dafür kein Bild-Werkzeug (Plan E2).

export type JpegPruefung =
  | { ok: true }
  | { ok: false; grund: "kein-jpeg" | "beschaedigt" }
  | { ok: false; grund: "metadaten"; gefunden: string[] };

/** Ob in den entfernten Metadaten ein Standort stand – „unklar“, wenn ein Exif-Block nicht lesbar war. */
export type Standort = "ja" | "nein" | "unklar";

export type JpegBereinigung =
  | { ok: true; daten: Uint8Array; entfernt: string[]; standort: Standort }
  | { ok: false; grund: "kein-jpeg" | "beschaedigt" };

const SOI = 0xd8;
const SOS = 0xda;
const EOI = 0xd9;
const COM = 0xfe;
const APP0 = 0xe0;
const APP1 = 0xe1;
const APP2 = 0xe2;
const APP15 = 0xef;

/** Im Exif-Block (TIFF-Aufbau) zeigt dieser Eintrag auf die Standort-Daten (GPS). */
const EXIF_TAG_GPS = 0x8825;
/** … dieser auf die Kamera-Liste (Exif-IFD), darin Aufnahmezeit und Zeitzone. */
const EXIF_TAG_EXIF_IFD = 0x8769;
const EXIF_TAG_AUFNAHMEZEIT = 0x9003;
const EXIF_TAG_ZEITZONE = 0x9011;
/** Ein Eintrag mit Text (ASCII) im TIFF-Aufbau. */
const TIFF_TYP_TEXT = 2;

function textAb(daten: Uint8Array, start: number, laenge: number): string {
  let ergebnis = "";
  for (let i = start; i < Math.min(start + laenge, daten.length); i++) {
    const b = daten[i] ?? 0;
    if (b === 0) break;
    ergebnis += String.fromCharCode(b);
  }
  return ergebnis;
}

function nameDesAbschnitts(kennung: number, inhalt: string): string {
  if (kennung === COM) return "Kommentar";
  if (kennung === APP1) {
    if (inhalt.startsWith("Exif")) return "Exif (Kamera, Zeit, Standort)";
    if (inhalt.startsWith("http://ns.adobe.com/xap")) return "XMP";
    return "APP1";
  }
  if (kennung === 0xed) return "IPTC/Photoshop";
  return `APP${kennung - APP0}`;
}

/** Ein Abschnitt, der Metadaten tragen kann – alles außer JFIF (APP0) und dem Farbprofil (ICC). */
function istMetadaten(kennung: number, inhalt: string): boolean {
  if (kennung === COM) return true;
  if (kennung < APP1 || kennung > APP15) return false;
  return !(kennung === APP2 && inhalt.startsWith("ICC_PROFILE"));
}

/** Ein geöffneter TIFF-Aufbau: Zahlen lesen in der richtigen Byte-Reihenfolge, null außerhalb. */
interface Tiff {
  u16: (p: number) => number | null;
  u32: (p: number) => number | null;
  /** Wo die Hauptliste (IFD0) beginnt. */
  ifd0: number;
}

/** Öffnet einen TIFF-Aufbau – null, wenn Kopf oder Byte-Reihenfolge nicht stimmen. */
function tiffOeffnen(tiff: Uint8Array): Tiff | null {
  if (tiff.length < 8) return null;
  const b0 = tiff[0];
  const b1 = tiff[1];
  let klein: boolean;
  if (b0 === 0x49 && b1 === 0x49)
    klein = true; // "II": kleinstes Byte zuerst
  else if (b0 === 0x4d && b1 === 0x4d)
    klein = false; // "MM": größtes Byte zuerst
  else return null;
  const sicht = new DataView(tiff.buffer, tiff.byteOffset, tiff.byteLength);
  const u16 = (p: number) => (p >= 0 && p + 2 <= tiff.length ? sicht.getUint16(p, klein) : null);
  const u32 = (p: number) => (p >= 0 && p + 4 <= tiff.length ? sicht.getUint32(p, klein) : null);
  if (u16(2) !== 42) return null;
  const ifd0 = u32(4);
  if (ifd0 === null) return null;
  return { u16, u32, ifd0 };
}

/** Die Stelle des Eintrags mit diesem Tag in der Liste ab `ifd` – null, wenn er fehlt oder die Liste nicht lesbar ist. */
function tiffEintrag(t: Tiff, ifd: number, tag: number): number | null {
  const anzahl = t.u16(ifd);
  if (anzahl === null) return null;
  for (let i = 0; i < anzahl; i++) {
    const eintrag = ifd + 2 + i * 12;
    const gelesen = t.u16(eintrag);
    if (gelesen === null) return null;
    if (gelesen === tag) return eintrag;
  }
  return null;
}

/** Der Text (ASCII) eines Eintrags, ohne die Null am Ende – null, wenn es kein Text ist oder er ins Leere zeigt. */
function tiffText(tiff: Uint8Array, t: Tiff, eintrag: number): string | null {
  if (t.u16(eintrag + 2) !== TIFF_TYP_TEXT) return null;
  const anzahl = t.u32(eintrag + 4);
  // Bis 4 Bytes steht der Text im Eintrag selbst, sonst zeigt der Eintrag auf ihn.
  const start = anzahl !== null && anzahl <= 4 ? eintrag + 8 : t.u32(eintrag + 8);
  if (anzahl === null || start === null || anzahl > 64 || start + anzahl > tiff.length) return null;
  let text = "";
  for (let i = start; i < start + anzahl; i++) {
    const b = tiff[i] ?? 0;
    if (b === 0) break;
    text += String.fromCharCode(b);
  }
  return text;
}

const EXIF_ZEIT_MUSTER = /^(\d{4}):(\d{2}):(\d{2}) (\d{2}):(\d{2}):(\d{2})$/;
const EXIF_VERSATZ_MUSTER = /^([+-])(\d{2}):(\d{2})$/;

/**
 * Die Aufnahmezeit aus einem Exif-Block (TIFF-Aufbau): „DateTimeOriginal“
 * in der Kamera-Liste, dazu die Zeitzone („OffsetTimeOriginal“), falls die
 * Kamera sie schreibt. Ergebnis als Zeitpunkt nach ISO 8601 –
 * „2026-10-19T14:03:22+02:00“, ohne Zeitzone nur die Ortszeit
 * „2026-10-19T14:03:22“ (ein Browser liest sie dann als Ortszeit des
 * Geräts). Null, wenn nichts Brauchbares drinsteht.
 */
export function exifAufnahmezeit(tiff: Uint8Array): string | null {
  const t = tiffOeffnen(tiff);
  if (!t) return null;
  const zeiger = tiffEintrag(t, t.ifd0, EXIF_TAG_EXIF_IFD);
  const exifIfd = zeiger === null ? null : t.u32(zeiger + 8);
  if (exifIfd === null) return null;
  const zeitEintrag = tiffEintrag(t, exifIfd, EXIF_TAG_AUFNAHMEZEIT);
  const zeit = zeitEintrag === null ? null : tiffText(tiff, t, zeitEintrag);
  const z = zeit === null ? null : EXIF_ZEIT_MUSTER.exec(zeit.trim());
  if (!z) return null;
  const [jahr, monat, tag, stunde, minute, sekunde] = z.slice(1).map(Number) as [number, number, number, number, number, number];
  // Kameras ohne gestellte Uhr schreiben „0000:00:00 00:00:00“ – das ist keine Zeit.
  const datum = new Date(Date.UTC(jahr, monat - 1, tag, stunde, minute, sekunde));
  if (jahr < 1990 || datum.getUTCMonth() !== monat - 1 || datum.getUTCDate() !== tag || stunde > 23 || minute > 59 || sekunde > 59)
    return null;
  const ortszeit = `${z[1]}-${z[2]}-${z[3]}T${z[4]}:${z[5]}:${z[6]}`;

  const versatzEintrag = tiffEintrag(t, exifIfd, EXIF_TAG_ZEITZONE);
  const versatz = versatzEintrag === null ? null : tiffText(tiff, t, versatzEintrag);
  const v = versatz === null ? null : EXIF_VERSATZ_MUSTER.exec(versatz.trim());
  if (!v || Number(v[2]) > 14 || Number(v[3]) > 59) return ortszeit;
  return `${ortszeit}${v[1]}${v[2]}:${v[3]}`;
}

/**
 * Sucht im Exif-Block (nach „Exif“ und zwei Nullbytes beginnt ein
 * TIFF-Aufbau) den Eintrag, der auf die GPS-Daten zeigt. „ja“, wenn er da
 * ist und die GPS-Liste mindestens einen Eintrag hat; „unklar“, wenn der
 * Block nicht lesbar ist.
 */
export function exifStandort(tiff: Uint8Array): Standort {
  const t = tiffOeffnen(tiff);
  if (!t) return "unklar";
  const { u16, u32, ifd0 } = t;
  const anzahl = u16(ifd0);
  if (anzahl === null) return "unklar";
  for (let i = 0; i < anzahl; i++) {
    const eintrag = ifd0 + 2 + i * 12;
    const tag = u16(eintrag);
    if (tag === null) return "unklar";
    if (tag !== EXIF_TAG_GPS) continue;
    const gpsIfd = u32(eintrag + 8);
    if (gpsIfd === null) return "unklar";
    const gpsAnzahl = u16(gpsIfd);
    if (gpsAnzahl === null) return "unklar";
    return gpsAnzahl > 0 ? "ja" : "nein";
  }
  return "nein";
}

/** XMP ist Text; ein Standort steht dort als „exif:GPSLatitude“ und „exif:GPSLongitude“. */
function xmpStandort(daten: Uint8Array, start: number, ende: number): Standort {
  let text = "";
  for (let i = start; i < Math.min(ende, daten.length); i++) text += String.fromCharCode(daten[i] ?? 0);
  return /GPSLatitude|GPSLongitude/.test(text) ? "ja" : "nein";
}

function standortZusammen(bisher: Standort, neu: Standort): Standort {
  if (bisher === "ja" || neu === "ja") return "ja";
  if (bisher === "unklar" || neu === "unklar") return "unklar";
  return "nein";
}

interface Abschnitt {
  kennung: number;
  /** Wo die Kennung steht (davor das 0xFF). */
  kennungPos: number;
  /** Wo der Inhalt beginnt (nach den zwei Längen-Bytes). */
  inhaltStart: number;
  /** Wo der Abschnitt endet (erstes Byte danach). */
  ende: number;
}

/**
 * Geht die Abschnitte bis zum Bildkopf (SOS) durch. Abschnitte mit Länge
 * gehen an `beiAbschnitt`, Marker ohne Länge (RST, TEM, EOI) an
 * `beiMarker`; was nach dem Bildkopf kommt (die Bilddaten), an `beiRest`.
 */
function abschnitteDurchgehen(
  daten: Uint8Array,
  beiAbschnitt: (a: Abschnitt) => void,
  beiMarker: (kennung: number) => void,
  beiRest: (ab: number) => void,
): "ok" | "kein-jpeg" | "beschaedigt" {
  if (daten.length < 4 || daten[0] !== 0xff || daten[1] !== SOI) return "kein-jpeg";
  let pos = 2;
  while (pos < daten.length) {
    if (daten[pos] !== 0xff) return "beschaedigt";
    // Füllbytes 0xFF überspringen
    while (daten[pos] === 0xff) pos++;
    const kennung = daten[pos];
    const kennungPos = pos;
    pos++;
    if (kennung === undefined) return "beschaedigt";
    if (kennung === EOI) {
      beiMarker(kennung);
      break;
    }
    // Abschnitte ohne Länge (RST0–RST7, TEM)
    if ((kennung >= 0xd0 && kennung <= 0xd7) || kennung === 0x01) {
      beiMarker(kennung);
      continue;
    }
    const hoch = daten[pos];
    const tief = daten[pos + 1];
    if (hoch === undefined || tief === undefined) return "beschaedigt";
    const laenge = (hoch << 8) | tief;
    if (laenge < 2 || pos + laenge > daten.length) return "beschaedigt";
    beiAbschnitt({ kennung, kennungPos, inhaltStart: pos + 2, ende: pos + laenge });
    pos += laenge;
    // Nach dem Bildkopf (SOS) folgen nur noch Bilddaten – dort stehen keine
    // Metadaten-Abschnitte mehr, die ein Handy schreibt.
    if (kennung === SOS) {
      beiRest(pos);
      break;
    }
  }
  return "ok";
}

export function jpegPruefen(daten: Uint8Array): JpegPruefung {
  const gefunden: string[] = [];
  const ergebnis = abschnitteDurchgehen(
    daten,
    (a) => {
      const inhalt = textAb(daten, a.inhaltStart, 32);
      if (istMetadaten(a.kennung, inhalt)) gefunden.push(nameDesAbschnitts(a.kennung, inhalt));
    },
    () => {},
    () => {},
  );
  if (ergebnis !== "ok") return { ok: false, grund: ergebnis };
  if (gefunden.length > 0) return { ok: false, grund: "metadaten", gefunden };
  return { ok: true };
}

/**
 * Entfernt alle Metadaten-Abschnitte aus einem JPEG, Byte für Byte, und
 * lässt alles andere – Bildkopf, Tabellen, Bilddaten – unangetastet. Sagt
 * dazu, was drinstand und ob ein Standort dabei war. Ein sauberes JPEG
 * kommt unverändert zurück.
 */
export function jpegMetadatenEntfernen(daten: Uint8Array): JpegBereinigung {
  const teile: Uint8Array[] = [daten.subarray(0, 2)];
  const entfernt: string[] = [];
  let standort: Standort = "nein";
  const ergebnis = abschnitteDurchgehen(
    daten,
    (a) => {
      const inhalt = textAb(daten, a.inhaltStart, 32);
      if (!istMetadaten(a.kennung, inhalt)) {
        teile.push(new Uint8Array([0xff]), daten.subarray(a.kennungPos, a.ende));
        return;
      }
      entfernt.push(nameDesAbschnitts(a.kennung, inhalt));
      if (a.kennung === APP1 && inhalt.startsWith("Exif")) {
        // Nach „Exif“ und zwei Nullbytes beginnt der TIFF-Aufbau.
        standort = standortZusammen(standort, exifStandort(daten.subarray(a.inhaltStart + 6, a.ende)));
      } else if (a.kennung === APP1 && inhalt.startsWith("http://ns.adobe.com/xap")) {
        standort = standortZusammen(standort, xmpStandort(daten, a.inhaltStart, a.ende));
      }
    },
    (kennung) => teile.push(new Uint8Array([0xff, kennung])),
    (ab) => teile.push(daten.subarray(ab)),
  );
  if (ergebnis !== "ok") return { ok: false, grund: ergebnis };
  if (entfernt.length === 0) return { ok: true, daten, entfernt, standort };
  const sauber = new Uint8Array(teile.reduce((summe, t) => summe + t.length, 0));
  let pos = 0;
  for (const t of teile) {
    sauber.set(t, pos);
    pos += t.length;
  }
  return { ok: true, daten: sauber, entfernt, standort };
}

/**
 * Die Aufnahmezeit eines JPEG aus seinem Exif-Block (`exifAufnahmezeit`) –
 * null, wenn das Foto keine trägt. Gedacht für das Rohfoto am Gerät, vor dem
 * Verkleinern. Es genügt der Anfang der Datei: Steht der Exif-Block vorne
 * (wie bei jeder Kamera), zählt er, auch wenn danach etwas abgeschnitten ist.
 */
export function jpegAufnahmezeit(daten: Uint8Array): string | null {
  let zeit: string | null = null;
  abschnitteDurchgehen(
    daten,
    (a) => {
      if (zeit !== null || a.kennung !== APP1 || !textAb(daten, a.inhaltStart, 6).startsWith("Exif")) return;
      // Nach „Exif“ und zwei Nullbytes beginnt der TIFF-Aufbau.
      zeit = exifAufnahmezeit(daten.subarray(a.inhaltStart + 6, a.ende));
    },
    () => {},
    () => {},
  );
  return zeit;
}

/** Die Abschnitte mit dem Bildkopf (SOF): Breite und Höhe. Nicht dabei: 0xC4, 0xC8, 0xCC (Tabellen). */
function istBildkopf(kennung: number): boolean {
  return kennung >= 0xc0 && kennung <= 0xcf && kennung !== 0xc4 && kennung !== 0xc8 && kennung !== 0xcc;
}

/**
 * Breite und Höhe eines JPEG, gelesen aus dem Bildkopf (SOF) – null, wenn
 * es kein lesbares JPEG ist. Ein Foto ohne Metadaten hat keine Angabe zur
 * Drehung mehr; die Maße sind also die, in denen es angezeigt wird.
 */
export function jpegMasse(daten: Uint8Array): { breite: number; hoehe: number } | null {
  let masse: { breite: number; hoehe: number } | null = null;
  const ergebnis = abschnitteDurchgehen(
    daten,
    (a) => {
      if (masse !== null || !istBildkopf(a.kennung) || a.inhaltStart + 5 > a.ende) return;
      // Inhalt: Genauigkeit (1 Byte), Höhe (2), Breite (2), …
      const hoehe = ((daten[a.inhaltStart + 1] ?? 0) << 8) | (daten[a.inhaltStart + 2] ?? 0);
      const breite = ((daten[a.inhaltStart + 3] ?? 0) << 8) | (daten[a.inhaltStart + 4] ?? 0);
      if (breite > 0 && hoehe > 0) masse = { breite, hoehe };
    },
    () => {},
    () => {},
  );
  return ergebnis === "ok" ? masse : null;
}
